import { call, debounce, fork, put, select, takeLatest } from '@redux-saga/core/effects';
import { first, get } from 'lodash';
import { authenticate, authenticateFailed, fetchCandidateUser, getEmailOtp, getEmailOtpSuccess, getPhoneFirebaseOtp, getPhoneFirebaseOtpSuccess, initialize, initializeComplete, storeCandidateUser, storeIdToken, storeQueryParams, storeStoredSession, unAuthenticate, updateTimezoneRequest, verifyEmailOtp, verifyFirebaseOtp, } from '@containers/Auth/slice';
import { captureException } from '@sentry/react';
import { fetchCountryCodeApi } from '@utils/fetchCountryCode';
import fetchData from '@utils/fetchData';
import { getFingerprintRequestId } from '@utils/fingerprintClient';
import initializeFirebase, { firebaseLogout, getFirebaseIdToken } from '@utils/firebase';
import { QUERY_PARAMS_STORAGE_KEY, RESCHEDULED_ORDER_ID, clearLocalStorage, localStorageGetItem, localStorageSetItem, } from '@utils/localStorageHelpers';
import postData from '@utils/postData';
import { catchError } from '@utils/sentry';
import client from '@utils/apollo';
import { getMappedGmtTimezoneToShortName, systemTimezone } from '@utils/dateHelpers';
import { authenticateFirebase, getAllParams, getStoredQueryParams, isTokenExpired, parseToken, signInCredential, signInPhone, } from './helpers';
import authQueries from './queries';
import { selectAuth, selectIdToken, selectQueryParams } from './selectors';
export const CUSTOM_TOKEN_CACHE_KEY = 'tv.cx.customTokenCache';
export function* initializeWorker() {
    try {
        yield call(initializeFirebase);
        let params = (yield call(getAllParams));
        const storedParams = (yield call(getStoredQueryParams));
        if (params.c || params.custom_token || params.tid || params.live_attendee_token) {
            yield call(firebaseLogout);
            yield call(clearLocalStorage);
        }
        else {
            params = { ...storedParams, ...params };
        }
        const latestOrderId = yield call(localStorageGetItem, RESCHEDULED_ORDER_ID);
        if (latestOrderId) {
            params = { ...params, order_id: latestOrderId };
        }
        yield put(storeQueryParams(params));
        // if token is not expired means it is authenticated
        const idToken = (yield call(getFirebaseIdToken));
        if (idToken && !isTokenExpired(idToken)) {
            const parsedToken = (yield call(parseToken, idToken));
            // SCH-2151: Reuse a cached custom token when the idToken hasn't changed
            // instead of minting a new one on every page load. The custom token is
            // needed downstream by ProviewContext for proctoring iframes.
            let customToken = null;
            try {
                const cached = sessionStorage.getItem(CUSTOM_TOKEN_CACHE_KEY);
                if (cached) {
                    const { forIdToken, customToken: cachedToken } = JSON.parse(cached);
                    if (forIdToken === idToken && cachedToken) {
                        customToken = cachedToken;
                    }
                }
            }
            catch (e) {
                // Malformed cache entry — ignore and re-fetch.
                captureException(e);
            }
            if (!customToken) {
                try {
                    const response = (yield call(postData, {
                        queryString: authQueries.GET_CUSTOM_TOKEN_BY_ID_TOKEN,
                        payload: {
                            id_token: idToken,
                        },
                        spreadPayload: true,
                        context: {
                            skipAuthorization: true,
                            skipHasuraRole: true,
                        },
                    }));
                    customToken = response?.data?.auth_get_custom_token?.custom_token || null;
                    if (customToken) {
                        try {
                            sessionStorage.setItem(CUSTOM_TOKEN_CACHE_KEY, JSON.stringify({ forIdToken: idToken, customToken }));
                        }
                        catch (e) {
                            // sessionStorage full or unavailable — proceed without caching.
                            if (!(e instanceof DOMException && (e.name === 'QuotaExceededError' || e.name === 'SecurityError'))) {
                                captureException(e);
                            }
                        }
                    }
                }
                catch (e) {
                    captureException(e);
                    customToken = null;
                }
            }
            const session = {
                accessToken: idToken,
                customToken,
            };
            yield put(storeStoredSession(session));
            yield put(authenticate({ ...session, parsedToken }));
        }
        else if (params.custom_token) {
            yield call(authenticateWorker, {
                custom_token: params.custom_token,
            });
        }
        else if (params.c) {
            if (params.section_type_id) {
                // Handle biometric authentication case with c_token
                yield call(authenticateWorker, {
                    c_token: params.c,
                });
            }
            else {
                // Handle regular authentication with hash_token
                yield call(authenticateWorker, {
                    hash_token: params.c,
                });
            }
        }
        else if (params.live_attendee_token) {
            yield call(authenticateWorker, {
                live_attendee_token: params.live_attendee_token,
            });
        }
        yield put(initializeComplete());
    }
    catch (error) {
        yield call(catchError, {
            title: 'Auth initializeWorker',
            error: error,
        });
    }
}
export function* getEmailOtpWorker({ payload }) {
    try {
        yield call(postData, {
            queryString: authQueries.OTP_REQUEST,
            payload: {
                username: payload?.username,
            },
            spreadPayload: true,
            context: {
                skipAuthorization: true,
                skipHasuraRole: true,
            },
        });
        yield put(getEmailOtpSuccess({ username: payload?.username }));
        if (payload?.callback?.onSuccess) {
            yield call(payload.callback.onSuccess);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'Email Otp Worker',
            error: error,
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* getPhoneFireBaseOtpWorker({ payload }) {
    try {
        const result = yield call(signInPhone, payload?.phone);
        if (result) {
            const { verificationId } = result;
            yield put(getPhoneFirebaseOtpSuccess({ verificationId, phone: payload?.phone }));
        }
        else {
            // TODO: handle no result case
            throw Error('Not able to generate the Id token');
        }
        if (payload.callback.onSuccess) {
            yield call(payload.callback.onSuccess);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'Firebase Otp Worker',
            error: error,
        });
        if (payload.callback.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* verifyEmailOtpWorker({ payload }) {
    try {
        const state = (yield select(selectAuth));
        const response = yield call(postData, {
            queryString: authQueries.VERIFY_OTP,
            payload: {
                username: payload?.username,
                tenant_id: state?.queryParams?.tid ? Number(state?.queryParams?.tid) : undefined,
                code: payload?.code,
            },
            context: {
                skipAuthorization: true,
                skipHasuraRole: true,
            },
        });
        const idToken = get(response, 'data.canx_verify_otp.data.id_token');
        if (idToken) {
            yield put(storeIdToken(idToken));
        }
        else {
            throw Error('Not able to generate the Id token');
        }
        if (payload?.callback?.onSuccess) {
            yield call(payload.callback.onSuccess);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'Verify Otp Worker',
            error: error,
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* verifyFirebaseOtpWorker({ payload }) {
    try {
        const state = (yield select(selectAuth));
        const result = yield call(signInCredential, state.verificationId, payload?.code);
        if (result) {
            const { user } = result;
            if (user) {
                // @ts-expect-error // TODO: stsTokenManager is not defined in User type custom type have to define
                const stsTokenManage = user?.stsTokenManager?.toJSON();
                yield put(storeIdToken(stsTokenManage?.accessToken));
            }
            else {
                // TODO: handle no token generation
                throw Error('Failed to generate access token from firebase');
            }
        }
        else {
            // TODO: no result
            throw Error('Not able to sign in with firebase');
        }
        if (payload?.callback?.onSuccess) {
            yield call(payload.callback.onSuccess);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'firebase verify Otp Worker',
            error: error,
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* signupWorker() {
    try {
        const idToken = (yield select(selectIdToken));
        const queryParams = (yield select(selectQueryParams));
        const response = (yield call(postData, {
            queryString: authQueries.SIGNUP,
            payload: {
                id_token: idToken,
                tenant_id: queryParams.tid ? Number(queryParams.tid) : undefined,
            },
            context: {
                skipAuthorization: true,
                skipHasuraRole: true,
            },
        }));
        const result = response?.data?.canx_signup?.[0];
        if (result && result.success) {
            const hashToken = result.data?.hash_token;
            yield call(authenticateWorker, hashToken ? { hash_token: hashToken } : { id_token: idToken });
        }
        else {
            const err = Array.isArray(result?.error_message) ? result?.error_message?.[0] : result?.error_message;
            throw Error(`Failed in signupWorker: ${err}`);
        }
    }
    catch (error) {
        yield put(authenticateFailed());
        yield call(catchError, {
            title: 'Authenticate User Worker',
            error: error,
        });
    }
}
export function* getLiveAttendeeCustomToken(live_attendee_token, fpRequestId) {
    const meetCandidateResponse = yield call(postData, {
        queryString: authQueries.REGISTER_MEET_CANDIDATE,
        payload: {
            jwt_token: live_attendee_token,
        },
        spreadPayload: true,
        context: {
            skipAuthorization: true,
            skipHasuraRole: true,
        },
    });
    const meetCandidateHashToken = meetCandidateResponse?.data?.ir_register_meet_candidate?.hash_token;
    if (!meetCandidateHashToken) {
        throw Error('Failed to get hash token from live attendee token.');
    }
    // Now get the custom token using the hash token
    const response = (yield call(postData, {
        queryString: authQueries.GET_CUSTOM_TOKEN,
        payload: {
            hash_token: meetCandidateHashToken,
            ...(fpRequestId ? { fp_request_id: fpRequestId } : {}),
        },
        spreadPayload: true,
        context: {
            skipAuthorization: true,
            skipHasuraRole: true,
        },
    }));
    return {
        response,
        hash_token: meetCandidateHashToken,
    };
}
export function* authenticateWorker({ hash_token, id_token, custom_token, live_attendee_token, c_token, }) {
    try {
        let customToken = custom_token;
        let response = null;
        const fpRequestId = customToken ? null : (yield call(getFingerprintRequestId));
        if (customToken) {
            response = null;
        }
        else if (id_token) {
            response = (yield call(postData, {
                queryString: authQueries.GET_CUSTOM_TOKEN_BY_ID_TOKEN,
                payload: {
                    id_token,
                    ...(fpRequestId ? { fp_request_id: fpRequestId } : {}),
                },
                spreadPayload: true,
                context: {
                    skipAuthorization: true,
                    skipHasuraRole: true,
                },
            }));
        }
        else if (live_attendee_token) {
            const result = yield call(getLiveAttendeeCustomToken, live_attendee_token, fpRequestId);
            response = result.response;
            hash_token = result.hash_token;
        }
        else if (c_token) {
            response = (yield call(postData, {
                queryString: authQueries.GET_CUSTOM_TOKEN_BY_C_HASH,
                payload: {
                    c_token,
                    ...(fpRequestId ? { fp_request_id: fpRequestId } : {}),
                },
                spreadPayload: true,
                context: {
                    skipAuthorization: true,
                    skipHasuraRole: true,
                },
            }));
        }
        else {
            response = (yield call(postData, {
                queryString: authQueries.GET_CUSTOM_TOKEN,
                payload: {
                    hash_token,
                    ...(fpRequestId ? { fp_request_id: fpRequestId } : {}),
                },
                spreadPayload: true,
                context: {
                    skipAuthorization: true,
                    skipHasuraRole: true,
                },
            }));
        }
        if (!customToken) {
            customToken = response?.data?.auth_get_custom_token?.custom_token;
        }
        if (customToken) {
            const idToken = (yield call(authenticateFirebase, {
                customToken,
            }));
            /*
              The idToken which is getting generate in the initial state from firebase
              doesn't contains the claims. So we have to collect the custom_token from the idToken
              and then authenticate the firebase with the custom_token. In return of that what idToken the
              firebase returns is the accessToken which we have to use to authorization
             */
            if (idToken) {
                const parsedToken = (yield call(parseToken, idToken));
                const session = {
                    accessToken: idToken,
                    customToken,
                    hash_token,
                };
                yield put(authenticate({ ...session, parsedToken }));
            }
            else {
                throw Error('Failed to generate idToken from firebase');
            }
        }
        else {
            throw Error('Not able to generate the custom token.');
        }
    }
    catch (error) {
        yield put(authenticateFailed());
        yield call(catchError, {
            title: 'Authenticate Worker',
            error: error,
        });
    }
}
export function* storeQueryParamsWorker() {
    try {
        const params = yield select(selectQueryParams);
        yield call(localStorageSetItem, QUERY_PARAMS_STORAGE_KEY, JSON.stringify(params));
    }
    catch (error) {
        yield call(catchError, {
            title: 'storeQueryParamsWorker',
            error: error,
        });
    }
}
export function* unAuthenticateWorker(action) {
    try {
        yield call(firebaseLogout);
        yield call(client.resetStore);
        if (action.payload?.shouldReload) {
            // yield call([window.location, 'reload']); // It will reload the page immediately
            // adding setTimeOut because want to wait for 1.5sec then reload the page, so user the read the error
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'UnAuthenticate Worker',
            error: error,
            skipToast: false,
        });
    }
}
export function* fetchCurrentCandidateUser({ payload }) {
    try {
        const queryParams = (yield select(selectQueryParams));
        const queryResponse = (yield call(fetchData, {
            queryString: authQueries.GET_CURRENT_CANDIDATE_USER,
            queryVariables: {},
            forceRefresh: true,
        }));
        // Get the IP-based country code as fallback
        let countryCode = (yield call(fetchCountryCodeApi));
        const canCandidate = first(queryResponse?.can_candidate);
        // Override the IP-based country code with the candidate's country if available
        if (canCandidate && canCandidate?.country?.trim()) {
            countryCode = canCandidate?.country.toUpperCase();
        }
        let authUser = queryResponse?.auth_user_me;
        const authUserTimezone = authUser?.timezone;
        const mappedSystemTimezone = (yield call(getMappedGmtTimezoneToShortName, systemTimezone));
        if (!queryParams?.invoice_number && authUserTimezone !== mappedSystemTimezone) {
            yield put(updateTimezoneRequest({ timezone: mappedSystemTimezone }));
            authUser = {
                ...authUser,
                timezone: mappedSystemTimezone,
            };
        }
        if (canCandidate && authUser) {
            const data = {
                ...canCandidate,
                user: authUser,
                countryCode,
            };
            yield put(storeCandidateUser(data));
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'Fetch Current Candidate User Worker',
            error: error,
            skipToast: false,
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* updateTimezoneWorker({ payload, }) {
    try {
        const { timezone, callback } = payload;
        const response = yield call(postData, {
            queryString: authQueries.UPDATE_USER_QUERY,
            payload: { timezone },
            // spreadPayload: true,
        });
        const result = response?.data?.auth_update_user;
        if (result?.success) {
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, result?.data);
            }
        }
        else {
            throw new Error(result?.error_message || 'Update failed');
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'Update User Timezone',
            error: error,
        });
        const callback = payload?.callback; // fallback if destructure failed
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* initializeWatcher() {
    yield debounce(1000, initialize.type, initializeWorker);
}
export function* getEmailOtpWatcher() {
    yield takeLatest(getEmailOtp?.type, getEmailOtpWorker);
}
export function* getPhoneFirebaseOtpWatcher() {
    yield takeLatest(getPhoneFirebaseOtp?.type, getPhoneFireBaseOtpWorker);
}
export function* verifyEmailOtpWatcher() {
    yield takeLatest(verifyEmailOtp.type, verifyEmailOtpWorker);
}
export function* verifyFirebaseOtpWatcher() {
    yield takeLatest(verifyFirebaseOtp.type, verifyFirebaseOtpWorker);
}
export function* signupWatcher() {
    yield takeLatest([storeIdToken.type], signupWorker);
}
export function* storeQueryParamsWatcher() {
    yield takeLatest(storeQueryParams.type, storeQueryParamsWorker);
}
export function* fetchCurrentCandidateUserWatcher() {
    yield takeLatest([fetchCandidateUser.type, authenticate.type], fetchCurrentCandidateUser);
}
export function* unAuthenticateWatcher() {
    yield takeLatest(unAuthenticate.type, unAuthenticateWorker);
}
export function* updateTimezoneWatcher() {
    yield takeLatest(updateTimezoneRequest.type, updateTimezoneWorker);
}
export default function* authRootSaga() {
    yield fork(initializeWatcher);
    yield fork(getEmailOtpWatcher);
    yield fork(getPhoneFirebaseOtpWatcher);
    yield fork(verifyEmailOtpWatcher);
    yield fork(verifyFirebaseOtpWatcher);
    yield fork(signupWatcher);
    yield fork(storeQueryParamsWatcher);
    yield fork(fetchCurrentCandidateUserWatcher);
    yield fork(unAuthenticateWatcher);
    yield fork(updateTimezoneWatcher);
}
